import { z } from 'zod';
import {
  PARTNER_NAME_MAX_LENGTH,
  PARTNER_NAME_MIN_LENGTH,
  PARTNER_NAME_BAD_STRUCTURE_REGEX,
  PARTNER_NAME_NO_NUMBERS_REGEX,
  PARTNER_NAME_VALID_CHARS_REGEX,
} from './const';
import { getPartnerNameErrorMessages } from './translations';

export type PartnerNameValidationMessages = {
  required?: string;
  tooLong?: string;
  containsNumbers?: string;
  containsInvalidCharacters?: string;
  invalid?: string;
};

export type CreatePartnerNameSchemaOptions = {
  allowEmpty?: boolean;
  // Per-rule messages. Any missing keys fall back to defaults
  messages?: PartnerNameValidationMessages;
};

const resolveMessages = ({ messages }: Pick<CreatePartnerNameSchemaOptions, 'messages'>) => {
  const errorMessages = getPartnerNameErrorMessages();
  return {
    required: messages?.required ?? errorMessages.required,
    tooLong: messages?.tooLong ?? errorMessages.tooLong,
    containsNumbers: messages?.containsNumbers ?? errorMessages.containsNumbers,
    containsInvalidCharacters:
      messages?.containsInvalidCharacters ?? errorMessages.containsInvalidCharacters,
    invalid: messages?.invalid ?? errorMessages.invalid,
  };
};

export const createPartnerNameSchema = (options: CreatePartnerNameSchemaOptions = {}) => {
  const { allowEmpty = false } = options;
  const messages = resolveMessages(options);

  const skip = (value: string) => value === '';

  return z
    .string()
    .max(PARTNER_NAME_MAX_LENGTH, { message: messages.tooLong })
    .min(allowEmpty ? 0 : PARTNER_NAME_MIN_LENGTH, { message: messages.required })
    .refine((value) => skip(value) || !PARTNER_NAME_NO_NUMBERS_REGEX.test(value), {
      message: messages.containsNumbers,
    })
    .refine((value) => skip(value) || PARTNER_NAME_VALID_CHARS_REGEX.test(value), {
      message: messages.containsInvalidCharacters,
    })
    .refine((value) => skip(value) || !PARTNER_NAME_BAD_STRUCTURE_REGEX.test(value), {
      message: messages.invalid,
    });
};
